import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { shareReplay, switchMap } from 'rxjs/operators'
import { Channel, ConnectedChannelAccountResponse } from '@tokeet-frontend/tv3-platform'
import { ChannelHelperService } from '@tv3/containers/channels/channel-connect/channel-helper.service'

interface ChannelAccountsCacheEntry {
  trigger: BehaviorSubject<void>
  stream: Observable<ConnectedChannelAccountResponse[]>
  subscribers: number
}

@Injectable({ providedIn: 'root' })
export class ChannelAccountsCacheService {
  private entries = new Map<string, ChannelAccountsCacheEntry>()

  constructor(private channelHelperService: ChannelHelperService) {}

  getAccounts(channel: Channel): Observable<ConnectedChannelAccountResponse[]> {
    const key = channel.id
    let entry = this.entries.get(key)

    if (!entry) {
      const trigger = new BehaviorSubject<void>(undefined)
      entry = {
        trigger,
        subscribers: 0,
        stream: trigger.pipe(
          switchMap(() => this.fetchAccounts(channel)),
          shareReplay({ bufferSize: 1, refCount: true })
        ),
      }
      this.entries.set(key, entry)
    }

    const cacheEntry = entry
    return new Observable<ConnectedChannelAccountResponse[]>((observer) => {
      cacheEntry.subscribers += 1
      const subscription = cacheEntry.stream.subscribe(observer)

      return () => {
        subscription.unsubscribe()
        cacheEntry.subscribers -= 1

        if (cacheEntry.subscribers === 0 && this.entries.get(key) === cacheEntry) {
          cacheEntry.trigger.complete()
          this.entries.delete(key)
        }
      }
    })
  }

  private fetchAccounts(channel: Channel): Observable<ConnectedChannelAccountResponse[]> {
    return this.channelHelperService.getChannelAccounts(channel) as Observable<ConnectedChannelAccountResponse[]>
  }

  invalidate(channel: Channel | string) {
    const key = typeof channel === 'string' ? channel : channel.id
    this.entries.get(key)?.trigger.next()
  }
}
